import { PASSENGER_COUNTS, POSITION_ORDER } from './seatPreferenceConstants';

export { PASSENGER_COUNTS, POSITION_ORDER } from './seatPreferenceConstants';

export const SEAT_PREFERENCES_STORAGE_KEY = 'seats-preferences';
export const SEAT_PREFERENCES_SCHEMA_VERSION = 1;

const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const normalizeNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
};

const normalizeFloor = (value, isPresent) => {
  if (!isPresent || value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : undefined;
  }
  if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return undefined;

  const numericFloor = Number(value.trim());
  return Number.isSafeInteger(numericFloor) && numericFloor > 0 ? String(numericFloor) : undefined;
};

const normalizePosition = (value, isPresent) => {
  if (!isPresent || value === null) return null;
  if (!Array.isArray(value) || value.length === 0) return undefined;
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) return undefined;
  }
  if (value.some((position) => !POSITION_ORDER.includes(position))) return undefined;

  const positions = new Set(value);
  return POSITION_ORDER.filter((position) => positions.has(position));
};

/**
 * Projects unknown input onto the complete persisted preference schema.
 * @param {*} value Potential preference.
 * @returns {{number: string, floor: string|null, position: Array<string>|null}|null} Safe value.
 */
export const sanitizeSeatPreference = (value) => {
  if (!isPlainObject(value)) return null;

  const number = normalizeNumber(value.number);
  const floor = normalizeFloor(value.floor, Object.prototype.hasOwnProperty.call(value, 'floor'));
  const position = normalizePosition(
    value.position,
    Object.prototype.hasOwnProperty.call(value, 'position'),
  );
  if (number === null || floor === undefined || position === undefined) return null;

  return { number, floor, position };
};

const sanitizeBucket = (value, passengerCount) => {
  if (!Array.isArray(value) || value.length !== passengerCount) return null;
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) return null;
  }

  const bucket = value.map(sanitizeSeatPreference);
  if (bucket.some((preference) => preference === null)) return null;

  for (let left = 0; left < bucket.length; left += 1) {
    for (let right = left + 1; right < bucket.length; right += 1) {
      if (bucket[left].number === bucket[right].number) {
        if (!bucket[left].floor || !bucket[right].floor) return null;
        if (bucket[left].floor === bucket[right].floor) return null;
      }
    }
  }

  return bucket;
};

const sanitizeStoredRoot = (value) => {
  if (
    !isPlainObject(value) ||
    value.version !== SEAT_PREFERENCES_SCHEMA_VERSION ||
    !isPlainObject(value.byPassengerCount)
  ) {
    return {};
  }

  return PASSENGER_COUNTS.reduce((buckets, count) => {
    const key = String(count);
    if (!Object.prototype.hasOwnProperty.call(value.byPassengerCount, key)) return buckets;

    const bucket = sanitizeBucket(value.byPassengerCount[key], count);
    if (bucket) buckets[key] = bucket;
    return buckets;
  }, {});
};

const resolveStorage = (storage) => {
  if (storage !== undefined) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

/**
 * Reads current storage and returns only independently valid one/two passenger buckets.
 * @param {Storage} [storage] Optional storage-compatible object.
 * @returns {Object<string, Array<Object>>} Validated buckets.
 */
export const readSeatPreferences = (storage) => {
  try {
    const target = resolveStorage(storage);
    if (!target || typeof target.getItem !== 'function') return {};

    const serialized = target.getItem(SEAT_PREFERENCES_STORAGE_KEY);
    if (serialized === null) return {};
    return sanitizeStoredRoot(JSON.parse(serialized));
  } catch (_error) {
    return {};
  }
};

/**
 * Replaces exactly one complete count bucket while preserving the other valid bucket.
 * @param {number} passengerCount Exact supported count.
 * @param {Array<Object>} orderedPreferences Complete ordered preferences.
 * @param {Storage} [storage] Optional storage-compatible object.
 * @returns {boolean} Whether the serialized payload was stored.
 */
export const writeSeatPreferences = (passengerCount, orderedPreferences, storage) => {
  try {
    if (!PASSENGER_COUNTS.includes(passengerCount)) return false;

    const bucket = sanitizeBucket(orderedPreferences, passengerCount);
    if (!bucket) return false;

    const target = resolveStorage(storage);
    if (!target || typeof target.getItem !== 'function' || typeof target.setItem !== 'function') {
      return false;
    }

    let currentBuckets = {};
    const currentSerialized = target.getItem(SEAT_PREFERENCES_STORAGE_KEY);
    if (currentSerialized !== null) {
      try {
        currentBuckets = sanitizeStoredRoot(JSON.parse(currentSerialized));
      } catch (_error) {
        return false;
      }
    }

    const payload = {
      version: SEAT_PREFERENCES_SCHEMA_VERSION,
      byPassengerCount: {
        ...currentBuckets,
        [String(passengerCount)]: bucket,
      },
    };
    const serialized = JSON.stringify(payload);
    target.setItem(SEAT_PREFERENCES_STORAGE_KEY, serialized);
    return true;
  } catch (_error) {
    return false;
  }
};
